var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _dec13, _class, _class2, _descriptor;

function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }

/**
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Component, html, Prop, Watch } from '@sick-davinci/ce-decorators';
import * as QuickChart from '@sick-davinci/quickchart';
import { style } from '../../_style/Common.style';
import { AbstractBarChart } from '../AbstractBarChart.js';
import { CssTools } from '../../_utils/CssTools.js';
import { BarChartStackedLayout } from './BarChartStackedLayout.js';
import { resolveCssColor, toCssColorValue } from '../../_utils/ColorTools.js';
import { createValueFormat } from '../../_utils/ValueFormat.js';
import { defaultShorthandLabels } from '../../_utils/NumberFormatter.js';
/**
 * A bar chart with stacked bars.
 * The elements of a bar are called 'segments'.
 * This chart does not support logarithmic value ranges.
 *
 * @example
 * ```html
 * <div style="width: 400px; height: 300px">
 *  <davinci-bar-chart-stacked palette="secondary" values="[[480, 510, 490],[800, 900, 750],[750, 1100, 650]]" bar-labels='["A","B","C"]' legend-labels='["one", "two", "three"]'></davinci-bar-chart-stacked>
 * </div>
 * ```
 */

export let BarChartStacked = (_dec = Component({
  tag: 'davinci-bar-chart-stacked',
  style
}), _dec2 = Prop(), _dec3 = Reflect.metadata("design:type", Boolean), _dec4 = Watch('shorthandLabels'), _dec5 = Reflect.metadata("design:paramtypes", []), _dec6 = Prop(), _dec7 = Reflect.metadata("design:type", Object), _dec8 = Prop(), _dec9 = Reflect.metadata("design:type", Object), _dec10 = Prop(), _dec11 = Reflect.metadata("design:type", Object), _dec12 = Prop(), _dec13 = Reflect.metadata("design:type", Object), _dec(_class = (_class2 = class BarChartStacked extends AbstractBarChart {
  constructor(...args) {
    super(...args);

    _initializerDefineProperty(this, "shorthandLabels", _descriptor, this);
  }

  shorthandLabelsWatcher() {
    this._updateStyle();
  }
  /**
   * One label can be assigned to each bar-stack.
   *
   * @default []
   */


  get barLabels() {
    return this._getCategoryLabels();
  }

  set barLabels(labels) {
    this._setCategoryLabels(labels);
  }
  /**
   * One icons can be assigned to each bar-stack.
   *
   * @default []
   */


  get icons() {
    return this._getCategoryIcons();
  }

  set icons(icons) {
    this._setCategoryIcons(icons);
  }
  /**
   * The colors to be used for the bar segments.
   * The first element denotes the color for the first segment of a bar,
   * the second element denotes the color for the second segment of a bar and so on.
   *
   * Each entry of this array is either interpreted as an index referring to the selected palette, or a css-color-value designation (e.g. 'purple', '#ff00ff', 'rgba(255,255,128,0.5)')
   *
   * If there are more segments than colors the array will be traversed from the beginning
   *
   * @default all colors of the current color-palette
   */


  get colors() {
    return this._getSeriesColors();
  }

  set colors(value) {
    this._setSeriesColors(value);
  }
  /**
   * The values to be used for the bars.
   *
   * A two dimensional array; the first dimension denotes the index of the bar, the second dimension the index of the segment.
   * e.g. 'value[2][4]' stores the value of the 5th segment of the 3rd bar
   *
   * Beware that the order of values should reflect the order of colors if the 'state' color palette is active.
   *
   * @default []
   */


  get values() {
    return this._getValues();
  }

  set values(values) {
    this._setValues(values);
  }

  _init() {
    var _this$shadowRoot;

    if (!this._api) {
      return;
    }

    const holder = (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.getElementById('holder');

    if (!holder) {
      return;
    }

    holder.appendChild(this._api.holder); // initial update of the data

    this._updateData(); // initial update of the style


    this._updateStyle();
  }
  /**
   * See base class
   */


  componentFirstRender() {
    this._init();
  }

  componentConnected() {
    super.componentConnected();
    this._api = QuickChart.createBarChartStacked(); // this could be a reattach without a following call for componentFirstRender -> reestablish QuickChart API

    this._init();
  }

  componentDisconnected() {
    super.componentDisconnected(); // We have been detached from the DOM -> clean up

    if (this._api) {
      var _this$shadowRoot2, _this$shadowRoot2$get;

      (_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 ? void 0 : (_this$shadowRoot2$get = _this$shadowRoot2.getElementById('holder')) === null || _this$shadowRoot2$get === void 0 ? void 0 : _this$shadowRoot2$get.removeChild(this._api.holder);

      this._api.dispose();
    }

    delete this._api;
  }
  /**
   * See base class
   */


  render() {
    var _this$legendLabels;

    // either show chart with surrounding legend or not
    if (((_this$legendLabels = this.legendLabels) === null || _this$legendLabels === void 0 ? void 0 : _this$legendLabels.length) > 0) {
      const colors = Array.from(new Set(this._getSeriesColors()));
      return html`
      <davinci-legend
        placement="N"
        small-font
        compact
        symbols='["box"]'
        symbol-size="${CssTools.REM2PIXEL(.7)}"
        .labels=${this.legendLabels}
        .palette=${this.palette}
        .colors=${colors}>
        ${this._getHolder()}
      </davinci-legend>
      `;
    } else {
      return html`${this._getHolder()}`;
    }
  }
  /**
   * @override
   */


  _updateData() {
    if (this._api === undefined) {
      return;
    }

    const data = this._createData();

    this._api.setData(data);
  }
  /**
   * @override
   */


  _updateStyle() {
    var _this$shadowRoot3;

    if (this._api === undefined) {
      return;
    }

    const host = (_this$shadowRoot3 = this.shadowRoot) === null || _this$shadowRoot3 === void 0 ? void 0 : _this$shadowRoot3.host;

    if (!host) {
      return;
    }

    const hostFontFamily = CssTools.GET_COMPUTED_FONT_FAMILY(host);

    const style = this._createStyle(hostFontFamily);

    this._api.setStyle(style);
  }
  /**
   * @override
   */


  _createStyle(hostFontFamily) {
    const valueFormatter = createValueFormat(this.shorthandLabels, this.decimalPlaces);

    const style = super._createStyle(hostFontFamily);

    style.barWidth = CssTools.REM2PIXEL(BarChartStackedLayout.BAR_WIDTH);
    style.minBarDistance = CssTools.REM2PIXEL(BarChartStackedLayout.MIN_BAR_DISTANCE + BarChartStackedLayout.BAR_WIDTH);
    style.maxBarDistance = CssTools.REM2PIXEL(BarChartStackedLayout.MAX_BAR_DISTANCE + BarChartStackedLayout.BAR_WIDTH);
    style.segmentColors = this._computeSegmentColors();
    style.remainderSegmentBorderColor = resolveCssColor('var(--davinci-color-background-elements-3)');
    style.remainderSegmentBorderWidth = BarChartStackedLayout.REMAINDER_SEGMENT_BORDER_WIDTH;
    style.valueFormat = {
      format(value, locale) {
        return valueFormatter(value, locale);
      }

    };
    style.valueLabelFont = {
      family: hostFontFamily,
      weight: 600,
      // semi-bold
      size: CssTools.REM2PIXEL(0.75),
      style: 'normal'
    };
    return style;
  } // A stacked bar chart does not support a logarithmic value range.
  // Also the minimum and maximum value must be computed separately.
  // Thus, a stacked bar chart has its own implementation of computing
  // the value range.


  _computeBarValueRange() {
    let minValue;
    let maxValue;

    if (this.valueRange !== undefined) {
      // The user defined a value-range -> take it!
      minValue = this.valueRange.start;
      maxValue = this.valueRange.end;
    }

    if (minValue === undefined) {
      minValue = this._computeBarMinValue();
    }

    if (maxValue === undefined) {
      maxValue = this._computeBarMaxValue();
    }

    if (minValue >= maxValue) {
      // eslint-disable-next-line
      console.error(minValue < maxValue, `value-range start is greater than or equal to value-range end: ${minValue} >= ${maxValue}`);
      maxValue = minValue + 1.0;
    }

    return {
      start: minValue,
      end: maxValue
    };
  }
  /**
   * Computes the minimum value to be used to scale the bars.
   *
   * @returns the minimum value to be used to scale the bars (always <= 0)
   */


  _computeBarMinValue() {
    const barValues = this._values;

    if (barValues === null) {
      // There are no values -> use 0.0 as default min value
      return 0.0;
    } // The minimum value will the smallest sum of negative values.


    let computedMinValue = Number.MAX_SAFE_INTEGER;
    let isEmpty = true;
    const size = barValues.length;

    for (let i = 0; i < size; i++) {
      const values = barValues[i];
      let negativeTotal = 0.0;

      for (let k = 0; k < values.length; k++) {
        isEmpty = false;
        const currentValue = values[k];

        if (currentValue < 0.0) {
          negativeTotal += currentValue;
        }
      }

      computedMinValue = Math.min(computedMinValue, negativeTotal);
    }

    if (isEmpty) {
      return 0.0;
    }

    return computedMinValue;
  }
  /**
   * Computes the maximum value to be used to scale the bars.
   *
   * @returns the maximum value to be used to scale the bars (always >= 0)
   */


  _computeBarMaxValue() {
    const barValues = this._values;

    if (barValues === null) {
      // There are no values -> use 100.0 as default max value
      return 100.0;
    } // The maximum value will the greatest sum of positive values.


    let computedMaxValue = Number.MIN_SAFE_INTEGER;
    let isEmpty = true;
    const size = barValues.length;

    for (let i = 0; i < size; i++) {
      const values = barValues[i];
      let positiveTotal = 0.0;

      for (let k = 0; k < values.length; k++) {
        isEmpty = false;
        const currentValue = values[k];

        if (currentValue > 0.0) {
          positiveTotal += currentValue;
        }
      }

      computedMaxValue = Math.max(computedMaxValue, positiveTotal);
    }

    if (isEmpty) {
      return 100.0;
    }

    return computedMaxValue;
  }
  /**
   * Computes the segment colors from the css styles of the segment-style elements.
   *
   * @returns the colors to be used for the segments of a bar
   */


  _computeSegmentColors() {
    return this.colors.map(color => toCssColorValue(color, this.palette));
  }

}, (_descriptor = _applyDecoratedDescriptor(_class2.prototype, "shorthandLabels", [_dec2, _dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return defaultShorthandLabels;
  }
}), _applyDecoratedDescriptor(_class2.prototype, "shorthandLabelsWatcher", [_dec4, _dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "shorthandLabelsWatcher"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "barLabels", [_dec6, _dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "barLabels"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "icons", [_dec8, _dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "icons"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "colors", [_dec10, _dec11], Object.getOwnPropertyDescriptor(_class2.prototype, "colors"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "values", [_dec12, _dec13], Object.getOwnPropertyDescriptor(_class2.prototype, "values"), _class2.prototype)), _class2)) || _class);
